import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";

export interface Package {
  id?: number;
  packageName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchPackages = createAsyncThunk(
  "package/fetchPackages",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      if (filters && filters.length > 0) {
        filters.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              filterField &&
              filterValue !== undefined &&
              filterValue !== null
            ) {
              if (typeof filterValue === "object" && filterValue !== null) {
                // Handle cases like { from_date: ..., to_date: ... }
                Object.entries(filterValue).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    params.append(key, String(value));
                  }
                  if (
                    (filterValue.from_date || filterValue.to_date) &&
                    (filterField === "created_at" ||
                      filterField === "updated_at")
                  ) {
                    if (filterField === "created_at") {
                      filterField = "created";
                    } else {
                      filterField = "updated";
                    }
                    params.append("filter_by", filterField);
                  }
                });
              } else {
                // Handle primitive values (string, number)
                params.append(filterField, filterValue);
              }
            }
          }
        );
      }

      const response: any = await Axios.get(`/package/?${params.toString()}`);
      // const response: any = await Axios.get(`/district/`, {
      //   params: {
      //     page,
      //     page_size: pageSize,
      //     search: search || undefined,
      //     filters: filters ? JSON.stringify(filters) : undefined,
      //   },
      // });

      // response.data;
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchPackages error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch packages");
    }
  }
);

export const fetchOnlyAllPackages = createAsyncThunk(
  "",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get("/package");
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package by ID"
      );
    }
  }
);

export const fetchPackageById = createAsyncThunk(
  "package/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/package/${id}/get/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package by ID"
      );
    }
  }
);

export const fetchPackageByDropDown = createAsyncThunk(
  "package/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/package/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package list"
      );
    }
  }
);

export const updatePackage = createAsyncThunk(
  "package/updatePackage",
  async ({ id, packageData }: { id: string; packageData: any }) => {
    const response: any = await Axios.put(
      `/package/${id}/update/`,
      packageData
    );
    return response;
  }
);

export const updateStatusInPkg = createAsyncThunk(
  "package/updateStatusInPkg",
  async (packageData: any, { rejectWithValue }) => {
    const payload = {
      is_active: packageData.is_active,
    };
    try {
      const response: any = await Axios.patch(
        `/package/${packageData.id}/update/`,
        payload
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update status");
    }
  }
);

export const addPackage = createAsyncThunk(
  "package/addPackage",
  async (packageData: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/package/create/`, packageData);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add package");
    }
  }
);

export const exportPackageCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const response = await Axios.get(`/package/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "package.csv",
  };
};
